// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPrice, { ProductPriceStyles } from "@rugby/Overrides/Components/ProductPrice";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    isPriceUOM = "isPriceUOM",
}

interface PriceProps extends WidgetProps {
    fields: {
        [fields.isPriceUOM]: boolean;
    };
}

type OwnProps = PriceProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    variantSelectionCompleted:
        !Object.keys(state.pages.productDetails.variantSelection).length ||
        state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsPriceStyles {
    productPrice?: ProductPriceStyles;
    configSeePrinceMessage?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const priceStyles: ProductDetailsPriceStyles = {
    productPrice: {
        price: {
            priceText: { size: 22 },
            unitOfMeasureText: { size: 15, weight: "bold" },
        },
    },
    configSeePrinceMessage: {
        color: "text.main",
        size: 22,
        css: css`
            font-weight: bold;
            letter-spacing: 0;
            line-height: 21px;
            margin-top: 8px;
            margin-bottom: 8px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
};

const styles = priceStyles;

const SectionWrapper = getStyledWrapper("section");

const ProductDetailsPrice: React.FC<OwnProps> = ({
    variantSelectionCompleted,
    productContext,
    productSettings,
    fields,
}) => {
    const hasSamePrices =
        productContext?.productInfo?.pricing?.additionalResults?.priceUOMNetPrice ===
        productContext?.productInfo?.pricing?.unitNetPriceDisplay;
    if ((!variantSelectionCompleted && !fields.isPriceUOM) || (!fields.isPriceUOM && hasSamePrices)) {
        return null;
    }

    return (
        <ProductPrice
            product={productContext}
            showLabel={false}
            showSavings={true}
            showSignIn={true}
            showPriceUOM={fields.isPriceUOM}
            showSavingsAmount={productSettings.showSavingsAmount}
            showSavingsPercent={productSettings.showSavingsPercent}
            extendedStyles={styles.productPrice}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsPrice)),
    definition: {
        displayName: "Price",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.isPriceUOM,
                displayName: "Is Price UOM",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
